import * as React from 'react'
import {
  AlertCircle as WarningCircleIcon,
  Factory as FactoryIcon,
  Shield as ShieldIcon
} from 'lucide-react'
import { Link } from 'react-router-dom'
import {
  useGetPlantSiteDetailQuery,
  useGetPlantSitesQuery,
  usePlantSessionPaint,
  type PlantSiteSummary
} from '@axilon-apps/domains-ontology-ui-state'
import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import { Empty } from '../../components/primitives/Empty'
import { plantSafetyIndexHref } from '../../lib/plant-links'
import { PlantHref } from './PlantHref'
import {
  SessionMarkAffordances,
  SessionRollupPill,
  countSessionMarks,
  sessionMarkClass
} from './session-mark-ui'

/**
 * `/plant/equipment` — Factory tab landing (display name; URL unchanged).
 *
 * Renders a responsive grid of every site in the ontology. Each tile
 * carries the line + equipment counts from `getPlantSites` so we don't
 * hit N+1 to populate the badges; clicking a tile drills into
 * the shared Plant entity link.
 */
const EquipmentPage = (): React.ReactElement => {
  const { data, isLoading, isError } = useGetPlantSitesQuery()
  const sites = data ?? []

  return (
    <div className="flex flex-1 flex-col gap-card">
      <header
        data-tour-id="plant-equipment-header"
        className="flex flex-wrap items-start justify-between gap-3"
      >
        <div>
          <h1 className="m-0 text-2xl font-semibold text-ink">Factory</h1>
          <p className="mt-1 text-sm text-ink-3">{equipmentSubtitle(sites)}</p>
        </div>
        <Button asChild variant="outline" size="sm">
          <Link to={plantSafetyIndexHref()}>
            <ShieldIcon />
            Safety Requirements
          </Link>
        </Button>
      </header>

      {isError ? (
        <div className="rounded-xl border bg-card p-6 text-card-foreground shadow-sm">
          <Empty
            title="Couldn't load sites"
            hint="Check the proxy is reachable and try again."
            icon={WarningCircleIcon}
          />
        </div>
      ) : isLoading && sites.length === 0 ? (
        <div className="rounded-xl border bg-card p-6 text-card-foreground shadow-sm">
          <div className="text-sm text-ink-3">Loading sites…</div>
        </div>
      ) : sites.length === 0 ? (
        <div className="rounded-xl border bg-card p-6 text-card-foreground shadow-sm">
          <Empty
            title="No sites yet"
            hint="Ingest a SCADA project to populate the plant."
            icon={FactoryIcon}
          />
        </div>
      ) : (
        <div
          data-tour-id="plant-equipment-sites"
          className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-[repeat(auto-fill,minmax(17.5rem,1fr))]"
        >
          {sites.map((site, index) => (
            <SiteTile
              key={site.id}
              site={site}
              dataTourId={index === 0 ? 'plant-site-tile' : undefined}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function SiteTile({
  site,
  dataTourId
}: {
  site: PlantSiteSummary
  dataTourId?: string
}): React.ReactElement {
  const {
    active,
    markFor,
    rollupByContainerId,
    touchedRollupByContainerId,
    deletedRollupByContainerId
  } = usePlantSessionPaint()
  const mark = markFor(site.id)
  // Write-log rollups often stop at the line (existing Site-[:HAS_LINE]->Line
  // edges are not re-written). When paint is active, walk the site's lines so
  // the Factory landing still shows a +/~/− pill.
  const { data: detail } = useGetPlantSiteDetailQuery(site.id, { skip: !active })
  const { added, modified, deleted } = React.useMemo(() => {
    const directAdded = rollupByContainerId.get(site.id) ?? 0
    const directTouched = touchedRollupByContainerId.get(site.id) ?? 0
    const directDeleted = deletedRollupByContainerId.get(site.id) ?? 0
    // Prefer live equipment ids from site detail (property updates omit
    // containment edges). Fall back to write-log rollups per counter.
    let lineAdded = 0
    let lineModified = 0
    let lineDeleted = 0
    let sawLiveEquipment = false
    for (const line of detail?.lines ?? []) {
      if (line.equipmentIds !== undefined) {
        sawLiveEquipment = true
        lineAdded += countSessionMarks(line.equipmentIds, markFor, 'added')
        lineModified += countSessionMarks(line.equipmentIds, markFor, 'touched')
      } else {
        lineAdded += rollupByContainerId.get(line.id) ?? 0
        lineModified += touchedRollupByContainerId.get(line.id) ?? 0
      }
      lineDeleted += deletedRollupByContainerId.get(line.id) ?? 0
      const lineMark = markFor(line.id)
      if (lineMark === 'added') lineAdded += 1
      else if (lineMark === 'touched') lineModified += 1
      else if (lineMark === 'deleted') lineDeleted += 1
    }
    if (sawLiveEquipment) {
      return {
        added: lineAdded,
        modified: lineModified,
        deleted: directDeleted > 0 ? directDeleted : lineDeleted
      }
    }
    return {
      added: directAdded > 0 ? directAdded : lineAdded,
      modified: directTouched > 0 ? directTouched : lineModified,
      deleted: directDeleted > 0 ? directDeleted : lineDeleted
    }
  }, [
    deletedRollupByContainerId,
    detail?.lines,
    markFor,
    rollupByContainerId,
    site.id,
    touchedRollupByContainerId
  ])
  return (
    <PlantHref
      data-tour-id={dataTourId}
      id={site.id}
      label="Site"
      node={site}
      className={sessionMarkClass(
        'flex min-w-0 items-center gap-3 rounded-xl border bg-card p-3.5 text-card-foreground no-underline shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground',
        mark
      )}
    >
      <span className="grid size-10 shrink-0 place-items-center rounded-lg bg-brand-soft text-brand">
        <FactoryIcon size={20} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="block min-w-0 flex-1 truncate text-sm font-semibold text-ink">
            {site.name}
          </span>
          <SessionMarkAffordances mark={mark} />
        </span>
        {site.description && (
          <span className="block truncate text-12 text-ink-3">{site.description}</span>
        )}
        <span className="mt-1.5 flex flex-wrap gap-1.5">
          <Badge variant="outline">
            {site.lineCount} {site.lineCount === 1 ? 'line' : 'lines'}
          </Badge>
          <Badge variant="outline">{site.equipmentCount} equipment</Badge>
          <SessionRollupPill added={added} modified={modified} deleted={deleted} />
        </span>
      </span>
    </PlantHref>
  )
}

function equipmentSubtitle(sites: PlantSiteSummary[]): string {
  if (sites.length === 0) {
    return 'Sites, lines, sublines, equipment, and manual stations with live tag values.'
  }
  let lineCount = 0
  let equipmentCount = 0
  for (const site of sites) {
    lineCount += site.lineCount
    equipmentCount += site.equipmentCount
  }
  return `${sites.length} ${sites.length === 1 ? 'site' : 'sites'} · ${lineCount} ${lineCount === 1 ? 'line' : 'lines'} · ${equipmentCount} equipment.`
}

export default EquipmentPage
